import React, { useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCopy,
  Link2,
  Link2Off,
  Loader2,
  Printer,
  Radio,
  RefreshCw,
} from 'lucide-react'
import { Button } from '../../ui/Button'
import { useRfidEncoder, RF_POWER_WARNING_THRESHOLD } from '../../../hooks/useRfidEncoder'
import { formatEpcBySegments, formatEpcPreview } from '../../../utils/epcComposer'
import {
  generateSingleLabelPrintHTML,
  openPrintWindow,
  type LabelData,
  type LabelTemplate,
} from '../../../utils/labelGenerator'
import { OverwriteConfirmation } from './overwrite-confirmation'

const PRINT_TEMPLATE: LabelTemplate = {
  id: 'standard',
  name: 'Standard (2" x 1")',
  width: 2,
  height: 1,
  unit: 'in',
  showName: true,
  showPrice: false,
  showSku: true,
  showDate: false,
  showLocation: false,
  showLogo: false,
  fontSize: 'medium',
  orientation: 'landscape',
  locationPosition: 'below',
  textStyle: 'normal',
  textColor: '#111111',
}

function StatusPill({ status }: { status: 'disconnected' | 'connecting' | 'connected' }) {
  const tone =
    status === 'connected'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
      : status === 'connecting'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
        : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${tone}`}>
      {status === 'connected' ? (
        <Link2 className="h-3.5 w-3.5" />
      ) : status === 'connecting' ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Link2Off className="h-3.5 w-3.5" />
      )}
      {status === 'connected' ? 'Encoder connected' : status === 'connecting' ? 'Connecting…' : 'Encoder disconnected'}
    </span>
  )
}

function VerificationRow({ label, passed }: { label: string; passed: boolean }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {passed ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-red-500" />
      )}
      <span className={passed ? 'text-gray-700 dark:text-gray-200' : 'text-red-600 dark:text-red-400'}>
        {label}
      </span>
    </div>
  )
}

export function RfidEncoderTab() {
  const encoder = useRfidEncoder()
  const [printError, setPrintError] = useState<string | null>(null)
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false)
  const [transcriptCopyState, setTranscriptCopyState] = useState<'idle' | 'copied' | 'failed'>(
    'idle'
  )
  const [frameLogTick, setFrameLogTick] = useState(0)

  const epcPreview = useMemo(() => {
    if (!encoder.scheme || !encoder.selectedVariant?.epcBase) return null
    const { fullPreview } = formatEpcPreview({
      epcBase: encoder.selectedVariant.epcBase,
      serialLength: encoder.scheme.serial_length,
    })
    return fullPreview
  }, [encoder.scheme, encoder.selectedVariant])

  const frameLog = useMemo(() => {
    void frameLogTick
    return encoder.getFrameLog()
  }, [encoder, frameLogTick])

  function handlePrintBarcode() {
    setPrintError(null)
    const variant = encoder.selectedVariant
    if (!variant) return

    const code = variant.barcode || variant.sku
    if (!code) {
      setPrintError('This variant has no barcode or SKU to print.')
      return
    }

    const labelData: LabelData = {
      barcode: code,
      codeType: 'code128',
      name: [variant.itemName, variant.colorName, variant.sizeName].filter(Boolean).join(' · '),
      sku: variant.sku,
      category: variant.category || undefined,
      date: new Date(),
    }

    const html = generateSingleLabelPrintHTML(labelData, PRINT_TEMPLATE, {
      barWidth: 3,
      barHeight: 100,
      margin: 20,
    })

    if (!html || !openPrintWindow(html))
      setPrintError('Could not open the print window. Allow pop-ups for this site.')
  }

  async function handleCopyTranscript() {
    const lines = encoder.getFrameLog().map((entry) => {
      const stamp = entry.at
      const side = entry.direction === 'sent' ? 'SENT' : 'RECV'
      const ops = entry.ops ? ` ${entry.ops}` : ''
      return `${stamp} ${side}${ops} ${entry.raw}`
    })
    try {
      await navigator.clipboard.writeText(lines.join('\n'))
      setTranscriptCopyState('copied')
      setTimeout(() => setTranscriptCopyState('idle'), 2000)
    } catch {
      setTranscriptCopyState('failed')
      setTimeout(() => setTranscriptCopyState('idle'), 2000)
    }
  }

  function handleToggleTranscript() {
    setIsTranscriptOpen((open) => {
      if (!open) setFrameLogTick((n) => n + 1)
      return !open
    })
  }

  if (encoder.isConfigLoading) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-10 text-center text-gray-500 dark:text-gray-400">
        Loading encoder configuration…
      </div>
    )
  }

  if (encoder.configError || !encoder.scheme) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-white dark:bg-gray-900 p-6 text-sm text-red-600 dark:text-red-400">
        {encoder.configError || 'No active EPC scheme available for this company.'}
      </div>
    )
  }

  const scheme = encoder.scheme
  const variant = encoder.selectedVariant

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">RFID Encoder</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
              Writes a {scheme.total_length * 4}-bit EPC ({scheme.total_length} hex characters) to a
              single tag on the desktop encoder, then verifies TID, PC word and EPC before the unit
              is recorded. Inventory quantities are never changed here.
            </p>
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
            <StatusPill status={encoder.status} />
            {encoder.status === 'connected' ? (
              <Button variant="secondary" onClick={() => void encoder.disconnect()}>
                Disconnect
              </Button>
            ) : (
              <Button onClick={() => void encoder.connect()} disabled={encoder.isConnecting}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {encoder.isConnecting ? 'Connecting…' : 'Connect encoder'}
              </Button>
            )}
            {encoder.deviceInfo && (
              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                {encoder.deviceInfo.serial ? `SN ${encoder.deviceInfo.serial}` : 'SN —'}
                {encoder.deviceInfo.firmware ? ` · fw ${encoder.deviceInfo.firmware}` : ''}
                {encoder.deviceInfo.hardware ? ` · hw ${encoder.deviceInfo.hardware}` : ''}
                {encoder.deviceInfo.rfPower !== null && encoder.deviceInfo.rfPower !== undefined
                  ? ` · RF ${encoder.deviceInfo.rfPower} dBm`
                  : ''}
                {encoder.deviceInfo.workMode !== null && encoder.deviceInfo.workMode !== undefined
                  ? ` · mode ${encoder.deviceInfo.workMode === 0 ? 'Answer' : encoder.deviceInfo.workMode}`
                  : ''}
              </p>
            )}
          </div>
        </div>

        {encoder.connectionError && (
          <div className="mt-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {encoder.connectionError}
          </div>
        )}

        {encoder.isWorkModeUnverified && (
          <div className="mt-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
            Work mode read-back is {encoder.deviceInfo?.workMode} (expected Answer = 0). Encoding
            is still allowed — if reads fail, set work mode to Answer in THY_ReaderSoft.
          </div>
        )}

        {encoder.isRfPowerHigh && (
          <div className="mt-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
            RF power is {encoder.deviceInfo?.rfPower} dBm; set it to 5-8 dBm in THY_ReaderSoft so
            the field only covers the encoding position. Above {RF_POWER_WARNING_THRESHOLD} dBm
            the reader can reach a garment that is not on the pad.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Select variant</h3>

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-gray-500">Product</span>
            <select
              value={encoder.selectedProductName ?? ''}
              onChange={(e) => encoder.selectProduct(e.target.value || null)}
              className="mt-1 w-full h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm text-gray-900 dark:text-gray-100"
            >
              <option value="">Choose a product…</option>
              {encoder.productNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-gray-500">Color</span>
            <select
              value={encoder.selectedColorName ?? ''}
              disabled={!encoder.selectedProductName}
              onChange={(e) => encoder.selectColor(e.target.value || null)}
              className="mt-1 w-full h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm text-gray-900 dark:text-gray-100 disabled:opacity-50"
            >
              <option value="">Choose a color…</option>
              {encoder.colorNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-gray-500">Size</span>
            <select
              value={variant?.productId ?? ''}
              disabled={!encoder.selectedColorName}
              onChange={(e) =>
                encoder.selectVariant(
                  encoder.sizeVariants.find((row) => row.productId === e.target.value) || null
                )
              }
              className="mt-1 w-full h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm text-gray-900 dark:text-gray-100 disabled:opacity-50"
            >
              <option value="">Choose a size…</option>
              {encoder.sizeVariants.map((row) => (
                <option key={row.productId} value={row.productId}>
                  {row.sizeName} — {row.sku}
                </option>
              ))}
            </select>
          </label>

          {variant && (
            <dl className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-gray-200 dark:border-gray-700">
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">SKU</dt>
                <dd className="font-mono text-gray-900 dark:text-gray-100">{variant.sku || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-gray-500">Barcode</dt>
                <dd className="font-mono text-gray-900 dark:text-gray-100">
                  {variant.barcode || '—'}
                </dd>
              </div>
            </dl>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">EPC to write</h3>

          <div className="rounded-lg bg-slate-950 text-emerald-300 font-mono text-sm sm:text-base px-4 py-3 break-all tracking-wide">
            {variant?.epcBase && epcPreview ? (
              formatEpcBySegments(scheme, epcPreview)
            ) : variant ? (
              <span className="text-amber-300">
                Incomplete —{' '}
                {variant.missingSegments.length > 0
                  ? `missing: ${variant.missingSegments.join(', ')}`
                  : variant.lengthIssues[0] || 'identifier codes are not encodable'}
              </span>
            ) : (
              <span className="text-slate-500">Select a variant to preview its EPC.</span>
            )}
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            The serial shown is a placeholder. The real serial is allocated by the database at the
            moment the encode starts, so it is never guessed on the client.
          </p>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button variant="secondary" onClick={handlePrintBarcode} disabled={!variant}>
              <Printer className="h-4 w-4 mr-2" />
              Print barcode
            </Button>
            <Button onClick={() => void encoder.startEncode()} disabled={!encoder.canEncode}>
              <Radio className="h-4 w-4 mr-2" />
              {encoder.isEncoding ? 'Encoding…' : 'UHF encode'}
            </Button>
          </div>

          {printError && (
            <p className="text-sm text-red-600 dark:text-red-400">{printError}</p>
          )}

          {encoder.status !== 'connected' && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Connect the encoder before writing.
            </p>
          )}
        </div>
      </div>

      {encoder.encodeError && (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-5 py-4 text-sm text-red-700 dark:text-red-300">
          <div className="font-semibold mb-1">Encode failed</div>
          {encoder.encodeError}
          {encoder.lastResult?.outcome === 'quarantined' && (
            <div className="mt-1">
              Unit {encoder.lastResult.serial} is quarantined and its serial is burned. Set this tag
              aside — its contents are unknown.
            </div>
          )}
        </div>
      )}

      {encoder.lastResult?.outcome === 'encoded' && (
        <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-5 py-4">
          <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-200 font-semibold">
            <CheckCircle2 className="h-5 w-5" />
            Encoded unit {encoder.lastResult.serial}
          </div>
          <div className="mt-2 font-mono text-sm break-all text-gray-900 dark:text-gray-100">
            {formatEpcBySegments(scheme, encoder.lastResult.epcFull)}
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <VerificationRow label="TID unchanged" passed={encoder.lastResult.verification.tidMatches} />
            <VerificationRow label="PC word correct" passed={encoder.lastResult.verification.pcMatches} />
            <VerificationRow label="EPC reads back" passed={encoder.lastResult.verification.epcMatches} />
          </div>
          {encoder.lastPreRead && (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 font-mono break-all">
              TID {encoder.lastPreRead.tid} · PC {encoder.lastResult.verification.readBackPc}
            </p>
          )}
          {encoder.lastResult.supersededRecordId && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
              The previous unit on this tag was retired.
            </p>
          )}
        </div>
      )}

      {encoder.stalePending.length > 0 && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-white dark:bg-gray-900 px-5 py-4">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 font-semibold text-sm">
            <AlertTriangle className="h-4 w-4" />
            Incomplete encode attempts ({encoder.stalePending.length})
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            These serials were reserved but never confirmed, usually a crash or a lost connection
            mid-write. The tags involved are treated as unknown and blocked.
          </p>
          <ul className="mt-3 space-y-1 text-xs font-mono">
            {encoder.stalePending.map((row) => (
              <li key={row.id} className="text-gray-700 dark:text-gray-300 break-all">
                {row.serial} · {row.epc_full} · TID {row.tid || '—'} ·{' '}
                {new Date(row.created_at).toLocaleString()}
              </li>
            ))}
          </ul>
        </div>
      )}

      {encoder.blocked && variant && (
        <OverwriteConfirmation
          scheme={scheme}
          classification={encoder.blocked}
          variant={variant}
          nextEpcPreview={epcPreview ? formatEpcBySegments(scheme, epcPreview) : ''}
          isEncoding={encoder.isEncoding}
          onCancel={encoder.cancelOverwrite}
          onConfirm={() => void encoder.confirmOverwrite()}
        />
      )}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex items-center justify-between gap-3 px-5 py-3">
          <button
            type="button"
            onClick={handleToggleTranscript}
            className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100"
          >
            {isTranscriptOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Bridge transcript
            <span className="font-normal text-gray-500 dark:text-gray-400">
              ({frameLog.length})
            </span>
          </button>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setFrameLogTick((n) => n + 1)}
              disabled={!isTranscriptOpen}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Refresh
            </Button>
            <Button variant="secondary" onClick={() => void handleCopyTranscript()}>
              <ClipboardCopy className="h-3.5 w-3.5 mr-1.5" />
              {transcriptCopyState === 'copied'
                ? 'Copied'
                : transcriptCopyState === 'failed'
                  ? 'Copy failed'
                  : 'Copy'}
            </Button>
          </div>
        </div>
        {isTranscriptOpen && (
          <pre className="max-h-72 overflow-auto border-t border-gray-200 dark:border-gray-700 bg-slate-950 text-emerald-300 text-[11px] leading-5 px-4 py-3 whitespace-pre-wrap break-all">
            {frameLog.length === 0
              ? 'No frames yet. Connect the encoder or run an encode to capture traffic.'
              : frameLog
                  .map((entry) => {
                    const side = entry.direction === 'sent' ? 'SENT' : 'RECV'
                    const ops = entry.ops ? ` ${entry.ops}` : ''
                    return `${entry.at} ${side}${ops} ${entry.raw}`
                  })
                  .join('\n')}
          </pre>
        )}
      </div>
    </div>
  )
}
